import { useEffect, useMemo, useRef, useState } from 'react';
import { useDebounceCallback } from 'usehooks-ts';
import { Box, HStack, VStack, View, Pressable, ScrollView, IconButton, CloseIcon, useTheme } from 'native-base';
import { filter, first, groupBy, indexOf, isEmpty, keyBy, map, orderBy, some } from 'lodash';
import { useTranslation } from 'react-i18next';

import { ButtonGroup, SearchField, Text } from '@pimm/base';
import { ResourceLoader } from '@app/components/shared';
import { formatToShortTimeOnly } from '@app/utils/date-formatter';
import { useSiteTime } from '../hooks';
import { formatName } from '@app/utils/string-formatter';
import { stringToDateLocal } from '@app/utils/date-formatter';
import { SmsRoles } from '@app/features/store-info/hooks';
import { useGetEmployeeSchedules } from '@app/features/employee-schedules/hooks';
import { useSiteConfig } from '../context';
import { useAppSelector } from '@app/store/store';
import { selectStoreEmployees } from '@app/store/selectors';
import { PasscodeLockIcon } from '../icons';
import { useUsersWithSiteKeys } from '@app/features/app';
import { UserBySiteKeyDto } from '@pimm/services/lib/sms-tenants';
import { EmployeeStatusEnum } from '@pimm/services/lib/sms-workforce';

export type SelectEmployeeProps = {
  caption?: string;
  isManager?: boolean;
  onClose?: () => void;
  onSelectEmployee: (_employee: UserBySiteKeyDto) => void;
};

export const SelectEmployee = ({ isManager, ...props }: SelectEmployeeProps) => {
  // TODO: Fix type issue
  const { colors } = useTheme();
  const { t } = useTranslation();
  const searchRef = useRef<any>(null);
  const employees = useAppSelector(state => selectStoreEmployees(state));
  const usersWithSiteKey = useUsersWithSiteKeys(state => state.users);
  const { siteConfig } = useSiteConfig();
  const siteId = siteConfig.id;
  const siteTime = useSiteTime();

  const [searchValue, setSearchValue] = useState<string>();
  const [employeeId, setEmployeeId] = useState<string | undefined>();
  const [roles] = useState<SmsRoles[]>(isManager ? [SmsRoles.MANAGER] : [SmsRoles.MANAGER, SmsRoles.USER]);
  const [tabFocus, setTabFocus] = useState<'scheduled' | 'all'>(isManager ? 'all' : 'scheduled');
  const [sections, setSections] = useState<Record<string, (UserBySiteKeyDto & { startTime?: string; endTime?: string })[]>>({
    all: [],
    scheduled: [],
  });

  const debounced = useDebounceCallback((employee: UserBySiteKeyDto) => {
    props.onSelectEmployee(employee);
    props.onClose?.();
  }, 500);

  const startEndOfDay = siteTime.toStartEndOfPart();
  const queryEmployeeSchedules = useGetEmployeeSchedules({
    siteId: siteId,
    startDate: startEndOfDay.startTime,
    endDate: startEndOfDay.endTime,
  });

  const eligibleUsers = useMemo(() => {
    const users = sections[tabFocus] ?? [];

    return users.filter(user =>
      formatName(user.firstName, user.lastName)
        .toLowerCase()
        .includes(searchValue?.toLowerCase() ?? ''),
    );
  }, [sections, tabFocus, searchValue]);

  const handlePressSelect = (employee: UserBySiteKeyDto) => () => {
    setEmployeeId(employee.id);
    debounced(employee);
  };

  const usersWithPinMap = useMemo(() => {
    return keyBy(usersWithSiteKey, 'userName');
  }, [usersWithSiteKey]);

  useEffect(() => {
    const employeeByIds = groupBy(queryEmployeeSchedules?.data?.data, 'employee.id');

    const eligibleUsers = map(
      filter(
        employees,
        employee =>
          employee.status === EmployeeStatusEnum.Active &&
          some(roles, role => employee.role?.trim().toLowerCase().includes(role.toLowerCase())) &&
          !!(employee.userName && usersWithPinMap[employee.userName]),
      ),
      user => {
        const schedule = user.id ? first(employeeByIds[user.id]) : undefined;
        const userBySiteKey = user.userName ? usersWithPinMap[user.userName] : undefined;

        return {
          ...userBySiteKey,
          startTime: schedule?.startTime,
          endTime: schedule?.endTime,
        };
      },
    );

    const sortedUsers = orderBy(eligibleUsers, [
      user => {
        const allowedRoles = [SmsRoles.MANAGER, SmsRoles.USER];
        const index = allowedRoles.findIndex(allowed => user.roles?.some(role => role.trim().toUpperCase() === allowed));
        return index === -1 ? 4 : index;
      },
      'firstName',
      'lastName',
      'startTime',
    ]);

    setSections({
      all: sortedUsers,
      scheduled: sortedUsers.filter(_ => !!_.startTime),
    });
  }, [employees, queryEmployeeSchedules?.data, usersWithPinMap]);

  return (
    <View w="full" h="full">
      <HStack alignItems="center" justifyContent="space-between" px={4} h="64px" borderBottomWidth={1}>
        <Box>
          <Text size="2xl" fontWeight={700} color="gray.900" lineHeight="sm">
            {isManager ? t('store_core:select_manager') : t('store_core:select_ops_leader')}
          </Text>
          <Text size="md" fontWeight={400} color="gray.600" lineHeight="sm">
            {isManager ? t('store_core:select_a_manager') : t('store_core:select_an_employee')}
          </Text>
        </Box>

        {!!props.onClose && (
          <IconButton
            rounded="lg"
            variant="unstyled"
            position="absolute"
            boxSize={8}
            p={0}
            right={2}
            _pressed={{ bg: 'gray.50' }}
            icon={<CloseIcon size="18px" color={colors.gray[700]} />}
            onPress={props.onClose}
          />
        )}
      </HStack>

      <VStack space={2} flex={1} p={4} pt={3} h="full">
        <ButtonGroup rounded="sm" value={tabFocus} onChange={setTabFocus}>
          {['scheduled', 'all'].map(tab => (
            <ButtonGroup.Item
              key={tab}
              value={tab}
              flex={1}
              badgeIcon={{
                _container: { minW: 7, ml: 1 },
                show: true,
                value: sections[tab]?.length ?? 0,
              }}
            >
              <Text size="md" color="gray.700" fontWeight={600} textTransform="capitalize">
                {t(`store_core:${tab}`)}
              </Text>
            </ButtonGroup.Item>
          ))}
        </ButtonGroup>

        <SearchField
          ref={searchRef}
          placeholder={isManager ? t('store_core:search_manager') : t('store_core:search_ops_leader')}
          value={searchValue}
          onChangeText={setSearchValue}
          onClear={() => setSearchValue(undefined)}
        />

        <Box rounded="xl" overflow="hidden" h="full" w="full" borderColor="gray.200" borderWidth={1} flex={1}>
          <ResourceLoader
            h="full"
            w="full"
            emptyMessage={t('store_core:no_available_employees')}
            isEmpty={isEmpty(eligibleUsers)}
            isLoading={queryEmployeeSchedules.isLoading}
          >
            <ScrollView key={tabFocus} h="full">
              <VStack key={tabFocus} borderBottomWidth={1}>
                {map(eligibleUsers, (employee, index) => {
                  const isActive = !!employeeId && employee?.id === employeeId;
                  const startTime = stringToDateLocal(employee.startTime);
                  const endTime = stringToDateLocal(employee.endTime);
                  const isPinSet = employee.pinIsSet;

                  return (
                    <Box key={employee.id} w="full" borderTopWidth={index ? 1 : 0}>
                      <Pressable rounded="lg" onPress={handlePressSelect(employee!)}>
                        {({ isHovered }) => (
                          <VStack alignItems="flex-start" justifyContent="center" py={2} px={3} bg={isActive ? 'primary.50' : 'white'}>
                            <HStack justifyContent="space-between" w="full">
                              <Text color={isActive ? 'primary.800' : 'black'} fontWeight={600} size="lg" lineHeight="md">
                                {formatName(employee?.firstName, employee?.lastName)}
                              </Text>
                              {!!startTime && !!endTime && (
                                <Text size="md" color={isActive ? 'primary.800' : 'gray.700'} fontWeight={500}>
                                  {`${formatToShortTimeOnly(startTime)} - ${formatToShortTimeOnly(endTime)}`}
                                </Text>
                              )}
                            </HStack>
                            <HStack justifyContent="space-between" w="full">
                              <Text size="md" color={isActive ? 'primary.800' : 'gray.600'} fontWeight={500}>
                                {employee.jobTitle}
                              </Text>
                              <PasscodeLockIcon size="sm" color={isPinSet ? colors.success[500] : colors.gray[500]} />
                            </HStack>
                          </VStack>
                        )}
                      </Pressable>
                    </Box>
                  );
                })}
              </VStack>
            </ScrollView>
          </ResourceLoader>
        </Box>
      </VStack>
    </View>
  );
};
